import { FC, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { 
  Button, 
  Card, 
  Label, 
  Table, 
  TextInput, 
  Modal,
  Spinner
} from "flowbite-react";
import PaginatedSelect from "@/components/PaginatedSelect";
import DateRangePresets from "@/components/reports/DateRangePresets";
import { HiSearch, HiDownload } from "react-icons/hi";
import { useLazyGetClientsQuery } from "@/api/clientApi/client";
import { useLazyGetClientConversationReportQuery, useDownloadClientConversationReportMutation } from "@/api/reportsApi/reports";
import { useSelectedCompany } from "@/context/SelectedCompanyContext";
import { useLazyGetChatRoomMessagesQuery } from "@/api/chatApi/chat";
import { BriefConversation, ConversationReportResponse } from "@/types/reports";
import { getErrorMessage } from "@/utils/requestUtils";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";
import { toast } from "@/lib/toast";

const ClientConversationAnalysis: FC = () => {
  const { t } = useTranslation(["reports"]);
  const today = new Date().toISOString().split("T")[0];
  const user = useCurrentUser();
  const isSuperAdmin = user?.agent_role === agentRoles.super_admin;
  // Company scope comes from the global header selector, not a per-report picker.
  const { selectedCompany: activeCompany } = useSelectedCompany();
  const selectedCompany = useMemo(
    () =>
      activeCompany
        ? { label: activeCompany.name, value: String(activeCompany.id) }
        : null,
    [activeCompany]
  );
  const [selectedClient, setSelectedClient] = useState<{label: string, value: string} | null>(null);
  // A visitor who signs in by email gets a new client record every visit, so
  // picking one record from the list shows only that visit. An email covers
  // the lot, and takes precedence when both are filled in.
  const [emailQuery, setEmailQuery] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [reportData, setReportData] = useState<ConversationReportResponse | null>(null);
  const [page, setPage] = useState(1);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isFetchingClients, setIsFetchingClients] = useState(false);
  const pageSize = 5;

  const [triggerGetClients, { isLoading: isClientsLoading }] = useLazyGetClientsQuery();

  // When the super admin switches company in the header, reset the client scope.
  useEffect(() => {
    setSelectedClient(null);
    setClientSearchTerm("");
    setClientOptions([]);
    setPage(1);
    setReportData(null);
  }, [activeCompany?.id]);

  useEffect(() => {
    if (isSuperAdmin && !selectedCompany) {
      setClientOptions([]);
      return;
    }
    setIsFetchingClients(true);
    const timer = setTimeout(() => {
      fetchClients(page, clientSearchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [page, clientSearchTerm, selectedCompany, isSuperAdmin]);

  const handleClientInputChange = (newValue: string, { action }: any) => {
    if (action === 'input-change') {
       setClientSearchTerm(newValue);
       setPage(1); 
    }
  };

  const fetchClients = async (pageNum: number, search: string) => {
      const params: any = { page: pageNum, pageSize: pageSize };
      if (search) {
          // Search both name and email; the backend ORs these filter fields.
          params.client_username = search;
          params.client_email = search;
      }
      if (isSuperAdmin && selectedCompany) {
          params.company_id = Number(selectedCompany.value);
      }

      try {
        const response = await triggerGetClients(params).unwrap();
        const items = Array.isArray(response) ? response : (response?.items || response?.data?.items || []);
        const total = response?.pagination?.totalPages || response?.totalPages || 1;

        setTotalPages(total);
        setClientOptions(items.map((client: any) => {
            const name = client.client_displayname || client.client_username || t("unknownClient");
            // Show the email next to the name so the right client is easy to spot.
            const label = client.client_email ? `${name} (${client.client_email})` : name;
            return { label, value: String(client.client_id) };
        }));
      } catch (error) {
          console.error("Failed to fetch clients", error);
      } finally {
        setIsFetchingClients(false);
      }
  };


  const [triggerReport, { isLoading: isReportLoading }] = useLazyGetClientConversationReportQuery();
  const [downloadReport, { isLoading: isDownloadLoading }] = useDownloadClientConversationReportMutation();
  const [triggerMessages, { data: messagesData, isLoading: isMessagesLoading }] = useLazyGetChatRoomMessagesQuery();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<BriefConversation | null>(null);

  const handleSearch = async () => {
    if (isSuperAdmin && !selectedCompany) {
      toast.warn(t("selectCompanyWarning"));
      return;
    }

    if ((!selectedClient && !emailQuery.trim()) || !startDate || !endDate) {
      toast.warn(t("selectClientDateWarning"));
      return;
    }

    if (startDate > endDate) {
        toast.error(t("startDateHigherThanEndDate"));
        return;
    }

    try {
      const result = await triggerReport({
        ...subjectParams(),
        start_date: startDate,
        end_date: endDate,
        ...(isSuperAdmin && selectedCompany ? { company_id: Number(selectedCompany.value) } : {})
      }).unwrap();
      setReportData(result);
    } catch (error: any) {
       toast.error(getErrorMessage(error));
    }
  };

  const subjectParams = (): { client_email?: string; client_id?: number } => {
    const email = emailQuery.trim();
    if (email) return { client_email: email };
    return { client_id: Number(selectedClient!.value) };
  };

  const handleDownload = async () => {
     if (isSuperAdmin && !selectedCompany) {
      toast.warn(t("selectCompanyWarning"));
      return;
    }

     if ((!selectedClient && !emailQuery.trim()) || !startDate || !endDate) {
      toast.warn(t("selectClientDateWarning"));
      return;
    }

    if (startDate > endDate) {
        toast.error(t("startDateHigherThanEndDate"));
        return;
    }

    try {
      toast.info(t("downloadingPdf"));
      const blob = await downloadReport({
        ...subjectParams(),
        start_date: startDate,
        end_date: endDate,
        ...(isSuperAdmin && selectedCompany ? { company_id: Number(selectedCompany.value) } : {})
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const subjectSlug = (emailQuery.trim() || selectedClient?.value || "client").replace(/[^a-zA-Z0-9._-]/g, "_");
      link.setAttribute('download', `chat-history-${subjectSlug}-${startDate}-${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("downloadComplete"));
    } catch (error: any) {
        toast.error(t("downloadFailed", { error: getErrorMessage(error) }));
    }
  };

  const handleRowClick = (conv: BriefConversation) => {
    setSelectedConversation(conv);
    setIsModalOpen(true);
    triggerMessages({ sessionKey: conv.session_key });
  };

  const formatDateDisplay = (dateStr: string) => {
      if (!dateStr) return "";
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
  };

  const formatMessageTime = (isoString: string) => {
      try {
          const d = new Date(isoString);
          return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      } catch (e) {
          return isoString;
      }
  };

  return (
    <div className="w-full">
      <div className="px-4 pt-6">
        <Card className="mb-6">
          <DateRangePresets
            className="mb-4"
            startDate={startDate}
            endDate={endDate}
            onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 items-end">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="client" value={t("selectClient")} />
              </div>
              <PaginatedSelect
                id="client"
                value={selectedClient}
                onChange={(option) => {
                    setSelectedClient(option);
                    if (!option) {
                        setClientSearchTerm("");
                        setPage(1);
                    }
                }}
                options={clientOptions}
                onInputChange={handleClientInputChange}
                isLoading={isClientsLoading}
                isFetching={isFetchingClients}
                placeholder={isSuperAdmin && !selectedCompany ? t("selectCompanyFirst") : t("searchClientPlaceholder")}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                menuMaxHeight={250}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="clientEmail" value={t("orByEmail")} />
              </div>
              <TextInput
                id="clientEmail"
                type="email"
                value={emailQuery}
                placeholder={t("clientEmailPlaceholder")}
                onChange={(e) => setEmailQuery(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("emailOverridesClient")}
              </p>
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="startDate" value={t("startDate")} />
              </div>
              <TextInput 
                id="startDate" 
                type="date"  
                required 
                value={startDate} 
                max={today}
                onChange={(e) => setStartDate(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key !== "Tab") e.preventDefault();
                }}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="endDate" value={t("endDate")} />
              </div>
              <TextInput 
                id="endDate" 
                type="date" 
                required 
                value={endDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key !== "Tab") e.preventDefault();
                }}
              />
            </div>
            
            <div className="flex gap-2">
              <Button color="primary" onClick={handleSearch} isProcessing={isReportLoading}>
                <HiSearch className="mr-2 h-5 w-5" />
                {t("search")}
              </Button>
              <Button color="light" onClick={handleDownload} disabled={!reportData || isDownloadLoading}>
                {isDownloadLoading ? <Spinner size="sm" className="mr-2" /> : <HiDownload className="mr-2 h-5 w-5" />}
                {t("downloadPdf")}
              </Button>
            </div>
          </div>
        </Card>

        {reportData && (
          <Card>
            <div className="mb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {t("analysisReportTitle")}
              </h2>
              <div className="text-sm text-gray-500">
                {t("clientLabel", { name: reportData.client.client_name })} <br/>
                {/* Say how many records were merged: a reader comparing this
                    against a single-client report needs to know why the counts
                    differ. */}
                {reportData.client.merged_record_count > 1 && (
                  <>
                    {t("mergedRecordsLabel", {
                      email: reportData.client.client_email,
                      count: reportData.client.merged_record_count,
                    })}{" "}
                    <br/>
                  </>
                )}
                {t("periodLabel", { start: formatDateDisplay(reportData.start_date), end: formatDateDisplay(reportData.end_date) })}
                {reportData.time_zone && (
                  <>
                    <br />
                    {t("timeZoneLabel", { zone: reportData.time_zone })}
                  </>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>{t("table.dateTime")}</Table.HeadCell>
                  <Table.HeadCell>{t("table.agentDetails")}</Table.HeadCell>
                  <Table.HeadCell>{t("table.start")}</Table.HeadCell>
                  <Table.HeadCell>{t("table.end")}</Table.HeadCell>
                  <Table.HeadCell>{t("table.duration")}</Table.HeadCell>
                  <Table.HeadCell>{t("table.llmToken")}</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {reportData.conversations.map((conv, idx) => (
                    <Table.Row
                      key={conv.session_history_id ?? `${conv.session_key}-${idx}`}
                      className="bg-white dark:border-gray-700 dark:bg-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleRowClick(conv)}
                    >
                      <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                        {conv.date} {conv.time}
                      </Table.Cell>
                      <Table.Cell>{conv.agent_details}</Table.Cell>
                      <Table.Cell>{conv.start_time}</Table.Cell>
                      <Table.Cell>{conv.end_time}</Table.Cell>
                      <Table.Cell>{conv.duration_formatted}</Table.Cell>
                      <Table.Cell>{conv.llm_tokens}</Table.Cell>
                    </Table.Row>
                  ))}
                  
                  {/* Totals Row */}
                  <Table.Row className="bg-gray-100 dark:bg-gray-700 font-bold">
                    <Table.Cell></Table.Cell>
                    <Table.Cell></Table.Cell>
                    <Table.Cell></Table.Cell>
                    <Table.Cell>{t("table.total")}</Table.Cell>
                    <Table.Cell>{reportData.totals.total_duration_formatted}</Table.Cell>
                    <Table.Cell>{reportData.totals.total_llm_tokens}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
            <div className="mt-4 text-right font-medium text-gray-900 dark:text-white">
               {t("totalCost", { tokens: reportData.totals.total_llm_tokens, cost: reportData.totals.total_llm_cost?.toFixed(2) || "0.00" })}
            </div>
          </Card>
        )}
      </div>

      <Modal show={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
        <Modal.Header>{t("modal.title")}</Modal.Header>
        <Modal.Body>
          {selectedConversation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm mb-4 border-b pb-4 dark:border-gray-600 dark:text-white">
                <div><strong>{t("modal.date")}</strong> {selectedConversation.date}</div>
                <div><strong>{t("modal.duration")}</strong> {selectedConversation.duration_formatted}</div>
                <div><strong>{t("modal.agent")}</strong> {selectedConversation.agent_details}</div>
              </div>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {isMessagesLoading ? (
                  <div className="text-center py-4"><Spinner /></div>
                ) : messagesData && Array.isArray(messagesData) ? (
                  messagesData.map((msg: any, idx: number) => {
                     const isClient = msg.sender_type === 1; 
                     const senderName = isClient ? (msg.sender_display_name || t("modal.clientSender")) : (msg.sender_display_name || t("modal.agentSender"));
                     
                     return (
                       <div key={idx} className={`p-2 rounded ${isClient ? 'bg-blue-50 dark:bg-blue-900 text-gray-900 dark:text-white' : 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white'}`}>
                         <span className="font-bold text-xs block mb-1">{formatMessageTime(msg.created_at)} {senderName}:</span>
                         <p className="text-sm">{msg.message_content}</p>
                       </div>
                     )
                  })
                ) : (
                  <p className="dark:text-white">{t("modal.noMessages")}</p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ClientConversationAnalysis;
